import type { Realm } from '@concord/domain';
import { Button, PublicShell } from '@concord/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { afterSignIn } from '../../lib/session';
import s from './auth.module.css';

/**
 * Landing page for the magic link. Signing in is a POST made by this page, so mail
 * scanners that prefetch the link's URL cannot spend it.
 */
export function AuthLink({ realm }: { realm: Realm }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const challengeId = params.get('c') ?? '';
    const token = params.get('t') ?? '';
    api<{ realm: Realm }>('/auth/verify-link', { body: { challengeId, token } })
      .then(async (r) => {
        navigate(await afterSignIn(qc, r.realm, r.realm === 'staff' ? '/console' : '/home'), {
          replace: true,
        });
      })
      .catch((e) => setError(errorMessage(e)));
  }, [params, navigate, qc]);

  const signIn = realm === 'staff' ? '/console/sign-in' : '/sign-in';
  return (
    <PublicShell narrow>
      <div className={s.card} aria-live="polite">
        {error ? (
          <>
            <h1>This link did not work</h1>
            <p className={s.sent}>{error}</p>
            <Button size="lg" onClick={() => navigate(signIn)}>
              Get a new code
            </Button>
          </>
        ) : (
          <>
            <h1>Signing you in</h1>
            <p className={s.sent}>One moment.</p>
          </>
        )}
      </div>
    </PublicShell>
  );
}
